// ============================================================================
// CHANGO V5.0 — <RoleGate> frontend permission component (T-D1-025)
// ============================================================================
// §20 pitfall #17: sales role MUST NEVER see cost / profit / margin. The
// authoritative guard is the backend `stripSensitiveForSales` middleware, but
// the SPA layers a second check so a single bug on either side cannot leak.
//
// Usage:
//   <RoleGate allow={['boss', 'finance']}>
//     <ProfitSummary />
//   </RoleGate>
//
// If `allow` matches the current user's role, children render unchanged.
// Otherwise we render `fallback` (default: null — silently hide).
// ============================================================================

import React from 'react';

import { useAuth } from '../hooks/useAuth.js';

export default function RoleGate({
  allow,
  children,
  fallback = null
}) {
  const { user } = useAuth();
  const role = user?.role;

  if (!role) return fallback;

  const allowed = Array.isArray(allow) ? allow : [allow];

  // §ST.10 Plan B: owner 不是 ops_users.role 的值，是 platform_ownership 标记。
  // 后端 /api/me 返回 is_owner=true 给 owner。前端 RoleGate 收到 allow=['owner']
  // 或 allow 含 'admin'/'owner' 时，把 is_owner=true 当 owner 通过。
  const isOwnerRequest = allowed.includes('owner') || allowed.includes('admin');
  if (isOwnerRequest && user?.is_owner === true) return <>{children}</>;

  if (!allowed.includes(role)) return fallback;

  return <>{children}</>;
}

/**
 * Inverse helper: only render for sales role (e.g., sales-specific coaching
 * banners). Symmetric to <RoleGate allow={['sales']}>.
 */
export function SalesOnly({ children, fallback = null }) {
  return <RoleGate allow={['sales']} fallback={fallback}>{children}</RoleGate>;
}

/**
 * Convenience: hide from sales specifically (the most common pattern).
 */
export function HideFromSales({ children, fallback = null }) {
  return <RoleGate allow={['boss', 'finance', 'admin']} fallback={fallback}>{children}</RoleGate>;
}
